/**
 * Chromium browsers (Chrome, Edge, Brave, Samsung Internet...) fire
 * `beforeinstallprompt` once the PWA install criteria are met. Capturing it
 * lets the app open the native install dialog from its own button instead of
 * relying on the browser's one-shot banner.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallPromptOutcome = 'accepted' | 'dismissed' | 'unavailable';

const INSTALL_PROMPT_READY_EVENT = 'comcolors:installprompt';

let deferredPrompt: BeforeInstallPromptEvent | null = null;

/** Call once, as early as possible (before React mounts). */
export const captureInstallPrompt = (): void => {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Hold the browser banner back: the in-app button will call prompt()
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event(INSTALL_PROMPT_READY_EVENT));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
};

export const hasInstallPrompt = (): boolean => deferredPrompt !== null;

/** Notifies when the browser makes the install prompt available. Returns the unsubscribe. */
export const onInstallPromptReady = (callback: () => void): (() => void) => {
  window.addEventListener(INSTALL_PROMPT_READY_EVENT, callback);
  return () => window.removeEventListener(INSTALL_PROMPT_READY_EVENT, callback);
};

/** Opens the browser's native install dialog. A captured event can be used once. */
export const promptInstall = async (): Promise<InstallPromptOutcome> => {
  const event = deferredPrompt;
  if (!event) return 'unavailable';
  deferredPrompt = null;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch (e) {
    console.error('Install prompt failed:', e);
    return 'unavailable';
  }
};
